import { useEffect, useState } from 'react';
import { PLAN_MESSAGE_KIND, PREVIEW_UI_TEMPLATE, projectJobStatus, projectType, TRIAL_POLL_MS } from '../../settings/shared/constants';
import { mergeAssistantMessages } from './chatUtils';
import { constructPreviewUrls } from '../../settings/shared/commonMethods';
import BillingService from '../../services/api/billing';
import { requestNotificationPermission, showSystemNotification } from "../../assets/js/notificationUtils";
import { extractPlanMessageFromWs, getQuestionIdFingerprint, isPlanChatMessage, readPlanProgress, writePlanMeta, extractPlanDocumentFromWs } from './planUtils';


const IN_PROGRESS_ASSISTANT_STATUSES = [
  projectJobStatus.PROCESSING,
  projectJobStatus.STREAMING,
];

export const TRIAL_USAGE_UPDATED_EVENT = 'sa-trial-usage-updated';

const lastMessageIsInProgressAssistant = (messages) => {
  const last = messages?.[messages.length - 1];
  return (
    last?.role === 'assistant' &&
    !isPlanChatMessage(last) &&
    IN_PROGRESS_ASSISTANT_STATUSES.includes(last.jobStatus)
  );
};

let planMessageSeq = 0;

const emitTrialUsage = (trial) => {
  if (!trial) return;
  try {
    window.dispatchEvent(new CustomEvent(TRIAL_USAGE_UPDATED_EVENT, { detail: trial }));
  } catch (e) {
    /* ignore */
  }
};

const fetchAndEmitTrialUsage = () =>
  BillingService.getTrialUsageSummary()
    .then((response) => {
      const trial = response?.trialUsageSummary;
      emitTrialUsage(trial);
      return trial;
    })
    .catch(() => null);

export const useChatMessages = (initialMessages, projectId, projectStatus, onPreviewUrlChange) => {
  const [state, setState] = useState({
    chatMessages: mergeAssistantMessages(initialMessages ?? [], projectJobStatus.SUCCESS, projectId),
    chatInput: '',
    streamingMessage: false,
    isTyping: false,
    messageFeedback: {},
    planAwaitingResponse: false,
  });

  useEffect(() => {
    if (projectStatus === projectJobStatus.PROCESSING || projectStatus === projectJobStatus.STREAMING) {
      setState((prev) => {
        const messages = [...prev.chatMessages];
        const last = messages[messages.length - 1];
        if (last && last.role === 'assistant' && !isPlanChatMessage(last)) {
          last.jobStatus = projectJobStatus.STREAMING;
        }
        return { ...prev, chatMessages: messages, streamingMessage: true };
      });
    }
  }, [projectStatus]);

  useEffect(() => {
    if (!state.streamingMessage) return undefined;

    let cancelled = false;
    const tick = () => {
      fetchAndEmitTrialUsage().finally(() => {
        /* ignore */
      });
    };

    tick();
    const id = window.setInterval(() => {
      if (!cancelled) tick();
    }, TRIAL_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [state.streamingMessage]);

  useEffect(() => {

    const handleMessageReceived = async (event) => {
      const message = event.detail;
      const wsProjectId = message?.ninfo?.projectId ?? message?.projectId;
      if (!projectId || String(projectId) !== String(wsProjectId ?? '')) return;

      const status = message?.ninfo?.status;
      const assistantMsg = message?.ninfo?.assistantMsg || '';
      const ninfo = message?.ninfo && typeof message.ninfo === 'object' ? message.ninfo : {};
      const planId = ninfo.planId || ninfo.plan_id || null;
      const buildAvailable =
        ninfo.buildAvailable === true ||
        ninfo.buildAvailable === 'true' ||
        ninfo.build_available === true;
      const planText = ninfo.planText || ninfo.plan_text || '';
      if (planId || planText || ninfo.buildAvailable != null || ninfo.build_available != null) {
        writePlanMeta(projectId, {
          ...(planId ? { planId } : {}),
          ...(ninfo.buildAvailable != null || ninfo.build_available != null
            ? { buildAvailable: Boolean(buildAvailable) }
            : {}),
          ...(planText ? { planText } : {}),
        });
      }
      const parsedPlan = extractPlanMessageFromWs(message);
      const planDocument = extractPlanDocumentFromWs(message);

      if (parsedPlan?.questions?.length) {
        const fingerprint = getQuestionIdFingerprint(parsedPlan.questions);
        const backendId =
          message?.ninfo?.messageId ||
          message?.ninfo?.id ||
          message?.id ||
          null;
        setState((prev) => {
          const hasDuplicate = (prev.chatMessages || []).some((msg) => {
            if (!isPlanChatMessage(msg) || msg.planStatus === "submitted") return false;
            if (backendId && String(msg.id) === String(backendId)) return true;
            return msg.planFingerprint === fingerprint;
          });
          if (hasDuplicate) {
            return { ...prev, isTyping: false, planAwaitingResponse: false, streamingMessage: false };
          }

          const lastUser = [...(prev.chatMessages || [])].reverse().find((msg) => msg.role === 'user');
          const intro =
            parsedPlan.intro ||
            assistantMsg ||
            "Let's understand your requirements before we start building.";
          const draft = {
            id: backendId || `plan-${fingerprint}-${++planMessageSeq}`,
            role: 'assistant',
            type: projectType.PLAN,
            messageKind: PLAN_MESSAGE_KIND,
            title: 'SuperAgentX AI',
            content: intro,
            createdAt: new Date(),
            projectId,
            jobStatus: projectJobStatus.SUCCESS,
            planIntro: intro,
            planMessage: parsedPlan.questions,
            planQuestions: parsedPlan.questions,
            planFingerprint: fingerprint,
            planSourcePrompt: lastUser?.prompt || lastUser?.content || '',
          };
          const stored = readPlanProgress(projectId, draft);
          const planMsg = {
            ...draft,
            planStatus: stored.submitted ? 'submitted' : 'active',
            planAnswers: stored.answers || {},
            planAdditionalInput: stored.additionalInput || '',
          };

          const messages = [...prev.chatMessages];
          const last = messages[messages.length - 1];
          if (
            last &&
            last.role === 'assistant' &&
            !isPlanChatMessage(last) &&
            !last.content &&
            IN_PROGRESS_ASSISTANT_STATUSES.includes(last.jobStatus)
          ) {
            messages[messages.length - 1] = planMsg;
          } else {
            messages.push(planMsg);
          }

          return {
            ...prev,
            chatMessages: messages,
            isTyping: false,
            streamingMessage: false,
            planAwaitingResponse: false,
          };
        });
        return;
      }

      switch (status) {
        case projectJobStatus.PROCESSING:
          setState((prev) => {
            if (lastMessageIsInProgressAssistant(prev.chatMessages)) {
              return prev;
            }
            return {
              ...prev,
              chatMessages: [...prev.chatMessages, {
                role: 'assistant',
                createdAt: new Date(),
                projectId,
                jobStatus: projectJobStatus.PROCESSING,
                content: '',
              }],
              isTyping: false,
              streamingMessage: true,
              planAwaitingResponse: false,
            };
          });
          break;

        case projectJobStatus.INTERRUPT:
          setState((prev) => ({
            ...prev,
            chatMessages: [...prev.chatMessages, {
              role: 'assistant',
              createdAt: new Date(),
              projectId,
              jobStatus: projectJobStatus.INTERRUPT,
              content: assistantMsg,
            }],
            isTyping: false,
            streamingMessage: false,
            planAwaitingResponse: false,
          }));
          break;

          case projectJobStatus.STREAMING:
            setState((prev) => {
              const messages = [...prev.chatMessages];
              const last = messages[messages.length - 1];

              if (
                last &&
                last.role === 'assistant' &&
                !isPlanChatMessage(last) &&
                IN_PROGRESS_ASSISTANT_STATUSES.includes(last.jobStatus)
              ) {
                last.content += (last.content ? "\n\n" : "") + assistantMsg;
                last.jobStatus = projectJobStatus.STREAMING;
              } else if (!lastMessageIsInProgressAssistant(messages) && assistantMsg) {
                messages.push({
                  role: 'assistant',
                  createdAt: new Date(),
                  projectId,
                  jobStatus: projectJobStatus.STREAMING,
                  content: assistantMsg,
                });
              }

              return { ...prev, chatMessages: messages, streamingMessage: true };
            });
            break;          

        case projectJobStatus.SUCCESS:
          case projectJobStatus.COMPLETED: {
            const planBody =
              (typeof planText === "string" && planText.trim()) ||
              (planDocument && assistantMsg) ||
              "";
            const isPlanResult = Boolean(planId || planText || ninfo.plan_event === "plan");
            let previewUrl = constructPreviewUrls(PREVIEW_UI_TEMPLATE, projectId);
            showSystemNotification(
            "Response Ready",
            "Your chat response is ready to view."
        );
          setState((prev) => {
            const chatMessages = [...(prev.chatMessages || [])];
            const last = chatMessages[chatMessages.length - 1];
            if (isPlanResult && planBody) {
              const planMsg = {
                role: "assistant",
                type: projectType.PLAN,
                createdAt: new Date(),
                projectId,
                jobStatus: projectJobStatus.SUCCESS,
                content: planBody,
                planText: planBody,
                planId: planId || undefined,
                buildAvailable: buildAvailable || undefined,
                planDocument: planDocument || undefined,
              };
              if (
                last &&
                last.role === "assistant" &&
                !isPlanChatMessage(last) &&
                IN_PROGRESS_ASSISTANT_STATUSES.includes(last.jobStatus)
              ) {
                chatMessages[chatMessages.length - 1] = { ...last, ...planMsg };
              } else {
                chatMessages.push(planMsg);
              }
            } else {
              const updated = chatMessages.map((msg, idx, arr) =>
                idx === arr.length - 1 ? { ...msg, jobStatus: projectJobStatus.SUCCESS } : msg
              );
              return {
                ...prev,
                chatMessages: updated,
                streamingMessage: false,
                isTyping: false,
                planAwaitingResponse: false,
              };
            }
            return {
              ...prev,
              chatMessages,
              streamingMessage: false,
              isTyping: false,
              planAwaitingResponse: false,
            };
          });
          if (onPreviewUrlChange && !isPlanResult) {
            onPreviewUrlChange(previewUrl);
          }
          break;
          }
        case projectJobStatus.FAILED:
          setState((prev) => {
            const last = prev.chatMessages?.[prev.chatMessages.length - 1];
            const errorContent = assistantMsg || 'Planning failed. Please try again.';
            const chatMessages = [...prev.chatMessages];
            if (last && last.role === 'assistant' && !isPlanChatMessage(last) && !last.content) {
              chatMessages[chatMessages.length - 1] = {
                ...last,
                jobStatus: projectJobStatus.FAILED,
                content: errorContent,
              };
            } else {
              chatMessages.push({
                role: 'assistant',
                createdAt: new Date(),
                projectId,
                jobStatus: projectJobStatus.FAILED,
                content: errorContent,
              });
            }
            return {
              ...prev,
              chatMessages,
              isTyping: false,
              streamingMessage: false,
              planAwaitingResponse: false,
            };
          });
          break;
        default:
          break;
      }
    };

    window.addEventListener('chatMessageReceived', handleMessageReceived);
    return () => window.removeEventListener('chatMessageReceived', handleMessageReceived);
  }, [projectId, onPreviewUrlChange]);

  return [state, setState];
};
